import React, { useState } from 'react';
import { Calculator as CalcIcon, Percent, Clock, DollarSign, Award, HelpCircle } from 'lucide-react';

export default function Calculator() {
  const [activeTab, setActiveTab] = useState('emi');

  // EMI Calculator State
  const [emiPrincipal, setEmiPrincipal] = useState(500000);
  const [emiRate, setEmiRate] = useState(10.5);
  const [emiTenure, setEmiTenure] = useState(60); // in months

  // FD Calculator State
  const [fdPrincipal, setFdPrincipal] = useState(100000);
  const [fdRate, setFdRate] = useState(7.5);
  const [fdTenure, setFdTenure] = useState(3); // in years

  // RD Calculator State
  const [rdMonthly, setRdMonthly] = useState(5000);
  const [rdRate, setRdRate] = useState(7.25);
  const [rdTenure, setRdTenure] = useState(24); // in months

  // Eligibility Calculator State
  const [monthlyIncome, setMonthlyIncome] = useState(45000);
  const [existingEmi, setExistingEmi] = useState(5000);
  const [elTenure, setElTenure] = useState(120); // in months

  // --- Calculations ---

  // 1. EMI Formula: [P x R x (1+R)^N]/[(1+R)^N-1]
  const calcEMI = () => {
    const P = emiPrincipal;
    const r = emiRate / 12 / 100;
    const n = emiTenure;
    if (r === 0) return { emi: P / n, interest: 0, total: P };

    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const totalPayment = emi * n;
    const totalInterest = totalPayment - P;

    return {
      emi: Math.round(emi),
      interest: Math.round(totalInterest),
      total: Math.round(totalPayment)
    };
  };

  // 2. FD Formula: A = P * (1 + r/4)^(4*t) (Quarterly compounding)
  const calcFD = () => {
    const P = fdPrincipal;
    const r = fdRate / 100;
    const t = fdTenure;
    const n = 4; // quarterly compounding
    const A = P * Math.pow(1 + r / n, n * t);
    const totalInterest = A - P;

    return {
      maturity: Math.round(A),
      interest: Math.round(totalInterest)
    };
  };

  // 3. RD Formula: M = R * [(1+i)^n - 1] / (1 - (1+i)^(-1/3)) approx
  const calcRD = () => {
    const P = rdMonthly;
    const r = rdRate / 100;
    const n = rdTenure;
    let totalMaturity = 0;
    let totalInvested = P * n;

    for (let i = 1; i <= n; i++) {
      const remainingTenureYears = (n - i + 1) / 12;
      const compoundFactor = Math.pow(1 + r / 4, 4 * remainingTenureYears);
      totalMaturity += P * compoundFactor;
    }

    const totalInterest = totalMaturity - totalInvested;

    return {
      invested: totalInvested,
      maturity: Math.round(totalMaturity),
      interest: Math.round(totalInterest)
    };
  };

  // 4. Loan Eligibility Formula
  const calcEligibility = () => {
    const maxEmiPossible = monthlyIncome * 0.5 - existingEmi;
    if (maxEmiPossible <= 0) return { loanAmount: 0, emi: 0 };

    const r = 11.5 / 12 / 100; // standard loan rate 11.5%
    const n = elTenure;
    const loanAmount = (maxEmiPossible * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n));

    return {
      loanAmount: Math.round(loanAmount),
      emi: Math.round(maxEmiPossible)
    };
  };

  const emiRes = calcEMI();
  const fdRes = calcFD();
  const rdRes = calcRD();
  const elRes = calcEligibility();

  return (
    <div className="bg-[#052046] border border-white/10 rounded-3xl overflow-hidden shadow-2xl text-white">
      {/* Header Tabs with space / gap between tabs */}
      <div className="p-4 bg-[#020c1a]/60 border-b border-white/5 flex gap-2 md:gap-3 overflow-x-auto no-scrollbar select-none">
        <button
          onClick={() => setActiveTab('emi')}
          className={`px-5 py-3 rounded-xl font-montserrat font-bold text-xs sm:text-sm whitespace-nowrap transition-all duration-300 flex items-center justify-center space-x-2 ${activeTab === 'emi'
              ? 'bg-[#B31825] text-white shadow-md scale-102'
              : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/5'
            }`}
        >
          <CalcIcon className="h-4 w-4" />
          <span>EMI Calculator</span>
        </button>
        <button
          onClick={() => setActiveTab('fd')}
          className={`px-5 py-3 rounded-xl font-montserrat font-bold text-xs sm:text-sm whitespace-nowrap transition-all duration-300 flex items-center justify-center space-x-2 ${activeTab === 'fd'
              ? 'bg-[#B31825] text-white shadow-md scale-102'
              : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/5'
            }`}
        >
          <Percent className="h-4 w-4" />
          <span>Fixed Deposit (FD)</span>
        </button>
        <button
          onClick={() => setActiveTab('rd')}
          className={`px-5 py-3 rounded-xl font-montserrat font-bold text-xs sm:text-sm whitespace-nowrap transition-all duration-300 flex items-center justify-center space-x-2 ${activeTab === 'rd'
              ? 'bg-[#B31825] text-white shadow-md scale-102'
              : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/5'
            }`}
        >
          <Clock className="h-4 w-4" />
          <span>Recurring Deposit (RD)</span>
        </button>
        <button
          onClick={() => setActiveTab('eligible')}
          className={`px-5 py-3 rounded-xl font-montserrat font-bold text-xs sm:text-sm whitespace-nowrap transition-all duration-300 flex items-center justify-center space-x-2 ${activeTab === 'eligible'
              ? 'bg-[#B31825] text-white shadow-md scale-102'
              : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/5'
            }`}
        >
          <Award className="h-4 w-4" />
          <span>Loan Eligibility</span>
        </button>
      </div>

      <div className="p-6 sm:p-8 md:p-10">
        {/* --- EMI TAB --- */}
        {activeTab === 'emi' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
            {/* Input Panel with Margin / Space */}
            <div className="space-y-8 pr-0 lg:pr-4">
              {/* Principal Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Loan Amount</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">₹{emiPrincipal.toLocaleString('en-IN')}</span>
                </div>
                <input
                  type="range"
                  min="50000"
                  max="10000000"
                  step="50000"
                  value={emiPrincipal}
                  onChange={(e) => setEmiPrincipal(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>₹50,000</span>
                  <span>₹1 Crore</span>
                </div>
              </div>

              {/* Rate Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Interest Rate (p.a.)</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">{emiRate}%</span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="20"
                  step="0.25"
                  value={emiRate}
                  onChange={(e) => setEmiRate(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>5%</span>
                  <span>20%</span>
                </div>
              </div>

              {/* Tenure Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Tenure (Months)</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">{emiTenure} M ({Math.round(emiTenure / 12 * 10) / 10} yrs)</span>
                </div>
                <input
                  type="range"
                  min="6"
                  max="360"
                  step="6"
                  value={emiTenure}
                  onChange={(e) => setEmiTenure(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>6 Months</span>
                  <span>360 Months</span>
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="bg-gradient-to-br from-[#03142c] to-[#010915] border border-white/10 rounded-3xl p-6 sm:p-8 flex flex-col justify-between space-y-8 shadow-xl">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-gray-400 block font-bold">Monthly EMI Installment</span>
                <span className="font-montserrat font-extrabold text-3xl sm:text-4xl text-[#E2B215] block mt-2 drop-shadow-sm font-mono">
                  ₹{emiRes.emi.toLocaleString('en-IN')}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 border-t border-white/5 pt-6 font-sans">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 block">Principal Amount</span>
                  <span className="text-sm sm:text-base font-bold text-white block mt-1 font-mono">₹{emiPrincipal.toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 block">Total Interest</span>
                  <span className="text-sm sm:text-base font-bold text-white block mt-1 font-mono">₹{emiRes.interest.toLocaleString('en-IN')}</span>
                </div>
                <div className="col-span-2 border-t border-white/5 pt-4 mt-2">
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 block">Total Amount Repayable</span>
                  <span className="text-lg font-extrabold text-[#00A86B] block mt-1 font-mono">₹{emiRes.total.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- FD TAB --- */}
        {activeTab === 'fd' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
            {/* Input Panel with Margin / Space */}
            <div className="space-y-8 pr-0 lg:pr-4">
              {/* Principal Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Deposit Principal</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">₹{fdPrincipal.toLocaleString('en-IN')}</span>
                </div>
                <input
                  type="range"
                  min="5000"
                  max="5000000"
                  step="5000"
                  value={fdPrincipal}
                  onChange={(e) => setFdPrincipal(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>₹5,000</span>
                  <span>₹50 Lakhs</span>
                </div>
              </div>

              {/* Rate Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Interest Rate (p.a.)</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">{fdRate}%</span>
                </div>
                <input
                  type="range"
                  min="3"
                  max="12"
                  step="0.1"
                  value={fdRate}
                  onChange={(e) => setFdRate(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>3%</span>
                  <span>12%</span>
                </div>
              </div>

              {/* Tenure Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Tenure (Years)</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">{fdTenure} Years</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  value={fdTenure}
                  onChange={(e) => setFdTenure(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>1 Year</span>
                  <span>10 Years</span>
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="bg-gradient-to-br from-[#03142c] to-[#010915] border border-white/10 rounded-3xl p-6 sm:p-8 flex flex-col justify-between space-y-8 shadow-xl">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-gray-400 block font-bold">Maturity Value</span>
                <span className="font-montserrat font-extrabold text-3xl sm:text-4xl text-[#00A86B] block mt-2 drop-shadow-sm font-mono">
                  ₹{fdRes.maturity.toLocaleString('en-IN')}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 border-t border-white/5 pt-6 font-sans">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 block">Principal Invested</span>
                  <span className="text-sm sm:text-base font-bold text-white block mt-1 font-mono">₹{fdPrincipal.toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 block">Total Interest Earned</span>
                  <span className="text-sm sm:text-base font-bold text-[#E2B215] block mt-1 font-mono">₹{fdRes.interest.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- RD TAB --- */}
        {activeTab === 'rd' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
            {/* Input Panel with Margin / Space */}
            <div className="space-y-8 pr-0 lg:pr-4">
              {/* Monthly Deposit Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Monthly Installment</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">₹{rdMonthly.toLocaleString('en-IN')}</span>
                </div>
                <input
                  type="range"
                  min="500"
                  max="100000"
                  step="500"
                  value={rdMonthly}
                  onChange={(e) => setRdMonthly(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>₹500</span>
                  <span>₹1 Lakh</span>
                </div>
              </div>

              {/* Rate Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Interest Rate (p.a.)</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">{rdRate}%</span>
                </div>
                <input
                  type="range"
                  min="3"
                  max="12"
                  step="0.1"
                  value={rdRate}
                  onChange={(e) => setRdRate(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>3%</span>
                  <span>12%</span>
                </div>
              </div>

              {/* Tenure Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Tenure (Months)</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">{rdTenure} Months</span>
                </div>
                <input
                  type="range"
                  min="6"
                  max="120"
                  step="6"
                  value={rdTenure}
                  onChange={(e) => setRdTenure(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>6 M</span>
                  <span>120 M</span>
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="bg-gradient-to-br from-[#03142c] to-[#010915] border border-white/10 rounded-3xl p-6 sm:p-8 flex flex-col justify-between space-y-8 shadow-xl">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-gray-400 block font-bold">RD Maturity Value</span>
                <span className="font-montserrat font-extrabold text-3xl sm:text-4xl text-[#00A86B] block mt-2 drop-shadow-sm font-mono">
                  ₹{rdRes.maturity.toLocaleString('en-IN')}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 border-t border-white/5 pt-6 font-sans">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 block">Total Invested</span>
                  <span className="text-sm sm:text-base font-bold text-white block mt-1 font-mono">₹{rdRes.invested.toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 block">Interest Accrued</span>
                  <span className="text-sm sm:text-base font-bold text-[#E2B215] block mt-1 font-mono">₹{rdRes.interest.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- LOAN ELIGIBILITY TAB --- */}
        {activeTab === 'eligible' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
            {/* Input Panel with Margin / Space */}
            <div className="space-y-8 pr-0 lg:pr-4">
              {/* Monthly Income Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Gross Monthly Income</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">₹{monthlyIncome.toLocaleString('en-IN')}</span>
                </div>
                <input
                  type="range"
                  min="10000"
                  max="500000"
                  step="5000"
                  value={monthlyIncome}
                  onChange={(e) => setMonthlyIncome(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>₹10,000</span>
                  <span>₹5 Lakhs</span>
                </div>
              </div>

              {/* Existing EMI Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Existing Monthly EMIs</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">₹{existingEmi.toLocaleString('en-IN')}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000"
                  step="1000"
                  value={existingEmi}
                  onChange={(e) => setExistingEmi(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>₹0</span>
                  <span>₹1 Lakh</span>
                </div>
              </div>

              {/* Tenure Input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400 font-bold uppercase tracking-wider text-[10px]">Tenure Option (Months)</span>
                  <span className="text-golden font-extrabold font-mono text-base bg-white/5 px-3 py-1 rounded-lg border border-white/5">{elTenure} Months ({Math.round(elTenure / 12)} Yrs)</span>
                </div>
                <input
                  type="range"
                  min="12"
                  max="240"
                  step="12"
                  value={elTenure}
                  onChange={(e) => setElTenure(Number(e.target.value))}
                  className="w-full accent-[#B31825] cursor-pointer h-2 bg-white/10 rounded-lg"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-semibold font-mono">
                  <span>12 Months</span>
                  <span>240 Months</span>
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="bg-gradient-to-br from-[#03142c] to-[#010915] border border-white/10 rounded-3xl p-6 sm:p-8 flex flex-col justify-between space-y-8 shadow-xl">
              <div>
                <span className="text-[10px] uppercase tracking-widest text-gray-400 block font-bold">Max Loan Eligibility</span>
                <span className="font-montserrat font-extrabold text-3xl sm:text-4xl text-[#E2B215] block mt-2 drop-shadow-sm font-mono">
                  {elRes.loanAmount > 0 ? `₹${elRes.loanAmount.toLocaleString('en-IN')}` : 'Not Eligible'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 border-t border-white/5 pt-6 font-sans">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 block">Eligible Monthly EMI</span>
                  <span className="text-sm sm:text-base font-bold text-[#00A86B] block mt-1 font-mono">₹{elRes.emi.toLocaleString('en-IN')}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 block">Interest Rate</span>
                  <span className="text-sm sm:text-base font-bold text-white block mt-1 font-mono">11.5% p.a.</span>
                </div>
                <div className="col-span-2 text-[10px] text-gray-500 mt-4 leading-normal font-light">
                  *Disclaimer: These calculations are indicative based on standard multipliers. Final eligibility is subject to credit appraisal check & regulatory approvals.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
